/**
 * The question-list builder shared by the survey builder (`SurveyFormDialog`)
 * and the volunteer signup intake editor: one `useFieldArray` over
 * `questions` (add, remove, and up / down buttons via `move()`, no drag and
 * drop; options, grid rows and grid columns reorder the same way), each
 * question edited in the flat `BuilderQuestion` shape (`surveyForm.ts`).
 *
 * Render it inside the host's `<Form {...form}>` (FormProvider): the per-type
 * switches set sibling fields through the form context. The host form's
 * values must carry `questions: BuilderQuestion[]`; everything else on the
 * form is the host's. `renderQuestionExtras(index, type)` appends host-only
 * controls to each question card (the intake editor's "Sensitive
 * information" switch).
 *
 * `isAnswered(questionId)` (volunteer intake only; surveys pass nothing and
 * get the unchanged editor): an ANSWERED question can be reworded, reordered,
 * made optional / required and given new options, never retyped or removed —
 * its type select is disabled, Remove becomes Retire / Restore (toggles
 * `retired`), and on its STORED options / rows / columns (the ids the editor
 * mounted with) Remove becomes Retire / Restore too; an option added in
 * this edit is still removed outright. Range inputs get a hint only: the
 * server refuses a narrowing (409 `volunteer_intake_change_refused`).
 * docs/VOLUNTEER_SIGNUP.md "Changing questions after answers".
 */
import { useState, type ReactNode } from 'react';
import { useFieldArray, useFormContext, useWatch, type Control, type UseFormReturn } from 'react-hook-form';
import { Archive, ArchiveRestore, ArrowDown, ArrowUp, Plus, Trash2, X } from 'lucide-react';
import {
  SURVEY_MAX_GRID_COLUMNS,
  SURVEY_MAX_GRID_ROWS,
  SURVEY_MAX_OPTIONS,
  SURVEY_QUESTION_TYPES,
  type SurveyQuestionType,
} from '@releve/shared';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { FormControl, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NumberInput } from '@/components/ui/number-input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';
import { surveyTypeLabel, type SurveyTranslate } from '../surveyLabels';
import { newBuilderQuestion, newOption, typeDefaults, type BuilderQuestion } from '../surveyForm';

/** The slice of a host form this editor owns. */
export interface QuestionListValues {
  questions: BuilderQuestion[];
}

export interface QuestionListEditorProps<TValues extends QuestionListValues = QuestionListValues> {
  control: Control<TValues>;
  /** The most questions the list accepts (Add is disabled at the cap). */
  max: number;
  /** Host-only controls appended to each question card. */
  renderQuestionExtras?: (index: number, type: SurveyQuestionType) => ReactNode;
  /**
   * Volunteer intake only: true when volunteers have answered the question
   * (Retire instead of Remove, type locked). Omitted → nothing is answered.
   */
  isAnswered?: (questionId: string) => boolean;
}

/** A field's inline error, beside its label (CLAUDE.md "Form errors"). */
export function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <span className="text-xs text-destructive">{message}</span>;
}

export function QuestionListEditor<TValues extends QuestionListValues>({
  control,
  max,
  renderQuestionExtras,
  isAnswered,
}: QuestionListEditorProps<TValues>) {
  const { t } = useTranslation('surveys');
  // The host's values extend QuestionListValues; the editor only touches `questions`.
  const listControl = control as unknown as Control<QuestionListValues>;
  const form = useFormContext<QuestionListValues>();
  const { fields, append, remove, move } = useFieldArray({ control: listControl, name: 'questions' });
  // The stored option / row / column ids per question, as the editor mounted (answered questions retire these).
  const [storedItems] = useState(() => storedItemIds(form.getValues('questions') ?? []));

  return (
    <>
      {fields.map((row, index) => (
        <QuestionEditor
          key={row.id}
          index={index}
          total={fields.length}
          form={form}
          onMove={move}
          onRemove={() => remove(index)}
          t={t}
          renderExtras={renderQuestionExtras}
          isAnswered={isAnswered}
          storedItems={storedItems}
        />
      ))}
      {fields.length === 0 && <p className="text-sm text-muted-foreground">{t('dialog.noQuestions')}</p>}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={fields.length >= max}
        onClick={() => append(newBuilderQuestion())}
      >
        <Plus className="mr-1 h-4 w-4" />
        {t('dialog.addQuestion')}
      </Button>
    </>
  );
}

/* ----------------------------- question editor --------------------------- */

interface QuestionEditorProps {
  index: number;
  total: number;
  form: UseFormReturn<QuestionListValues>;
  onMove: (from: number, to: number) => void;
  onRemove: () => void;
  t: SurveyTranslate;
  renderExtras?: QuestionListEditorProps['renderQuestionExtras'];
  isAnswered?: QuestionListEditorProps['isAnswered'];
  storedItems: StoredItems;
}

/** Question id → the ids of its options, rows and columns when the editor mounted. */
type StoredItems = ReadonlyMap<string, ReadonlySet<string>>;

function storedItemIds(questions: readonly BuilderQuestion[]): StoredItems {
  return new Map(
    questions.map((q) => [q.id, new Set([...q.options, ...q.rows, ...q.columns].map((o) => o.id))] as const),
  );
}

const HAS_OPTIONS: ReadonlySet<SurveyQuestionType> = new Set(['single_choice', 'dropdown', 'multi_choice']);
const IS_GRID: ReadonlySet<SurveyQuestionType> = new Set(['choice_grid', 'checkbox_grid']);
const SCALE_MAX_CHOICES = Array.from({ length: 9 }, (_, i) => i + 2); // 2..10
const RATING_MAX_CHOICES = Array.from({ length: 8 }, (_, i) => i + 3); // 3..10

/** A muted one-line hint under a locked control. */
function LockHint({ children }: { children: ReactNode }) {
  return <p className="text-xs text-muted-foreground">{children}</p>;
}

/** The "Retired" marker beside a retired question or option. */
function RetiredLabel({ t }: { t: SurveyTranslate }) {
  return <span className="shrink-0 text-xs font-medium text-muted-foreground">{t('surveys:dialog.retired')}</span>;
}

function QuestionEditor({ index, total, form, onMove, onRemove, t, renderExtras, isAnswered, storedItems }: QuestionEditorProps) {
  const { control } = form;
  const type = useWatch({ control, name: `questions.${index}.type` });
  const questionId = useWatch({ control, name: `questions.${index}.id` });
  const retired = useWatch({ control, name: `questions.${index}.retired` }) === true;
  const answered = isAnswered?.(questionId) ?? false;
  const number = index + 1;
  const toggleRetired = () => form.setValue(`questions.${index}.retired`, !retired, { shouldDirty: true });
  // Only an answered question's STORED items retire; anything else is removed outright.
  const retirableIds = answered ? storedItems.get(questionId) : undefined;
  const required = useWatch({ control, name: `questions.${index}.required` }) === true;
  const rangeHint = answered ? <LockHint>{t('surveys:dialog.answeredRangeHint')}</LockHint> : null;

  const changeType = (next: SurveyQuestionType) => {
    form.setValue(`questions.${index}.type`, next, { shouldDirty: true });
    const defaults = typeDefaults(next);
    if (next === 'scale' || next === 'rating') {
      form.setValue(`questions.${index}.min`, defaults.min, { shouldDirty: true });
      form.setValue(`questions.${index}.max`, defaults.max, { shouldDirty: true });
    } else if (next === 'number') {
      form.setValue(`questions.${index}.min`, '', { shouldDirty: true });
      form.setValue(`questions.${index}.max`, '', { shouldDirty: true });
    }
    if (next === 'section') form.setValue(`questions.${index}.required`, false, { shouldDirty: true });
  };

  return (
    <div
      role="group"
      aria-label={t('surveys:dialog.questionRow', { number })}
      className={cn('space-y-3 rounded-md border bg-background p-4', retired && 'border-dashed bg-muted/40')}
    >
      {retired && (
        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          <RetiredLabel t={t} />
          <span>{t('surveys:dialog.retiredQuestionNote')}</span>
        </p>
      )}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)_auto] md:items-end">
        <FormField
          control={control}
          name={`questions.${index}.label`}
          render={({ field, fieldState }) => (
            <FormItem>
              <div className="flex items-center gap-2">
                <FormLabel>{t('surveys:dialog.fields.question')}</FormLabel>
                <FieldError message={fieldState.error?.message} />
              </div>
              <FormControl>
                <Input
                  {...field}
                  placeholder={t('surveys:dialog.placeholders.question')}
                  className={fieldState.error ? 'border-destructive' : undefined}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name={`questions.${index}.type`}
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center gap-2">
                <FormLabel>{t('surveys:dialog.fields.type')}</FormLabel>
              </div>
              {/* A div of its own: Radix renders a hidden native <select> beside the
                  trigger, and FormItem's space-y would add its margin below the field,
                  lifting this column 8px above its neighbours. */}
              <div>
                <Select value={field.value} onValueChange={(v) => changeType(v as SurveyQuestionType)} disabled={answered}>
                  <FormControl>
                    <SelectTrigger aria-label={t('surveys:dialog.fields.type')}>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {SURVEY_QUESTION_TYPES.map((value) => (
                      <SelectItem key={value} value={value}>
                        {surveyTypeLabel(value, t)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {answered && <LockHint>{t('surveys:dialog.answeredTypeHint')}</LockHint>}
            </FormItem>
          )}
        />
        <div className="flex gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={index === 0}
            onClick={() => onMove(index, index - 1)}
            aria-label={t('surveys:dialog.moveUp', { number })}
            title={t('surveys:dialog.moveUp', { number })}
          >
            <ArrowUp className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={index === total - 1}
            onClick={() => onMove(index, index + 1)}
            aria-label={t('surveys:dialog.moveDown', { number })}
            title={t('surveys:dialog.moveDown', { number })}
          >
            <ArrowDown className="h-4 w-4" />
          </Button>
          {answered ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={toggleRetired}
              aria-label={t(retired ? 'surveys:dialog.restoreQuestion' : 'surveys:dialog.retireQuestion', { number })}
              title={t(retired ? 'surveys:dialog.restoreQuestion' : 'surveys:dialog.retireQuestion', { number })}
            >
              {retired ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
            </Button>
          ) : (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onRemove}
              aria-label={t('surveys:dialog.removeQuestion', { number })}
              title={t('surveys:dialog.removeQuestion', { number })}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      <FormField
        control={control}
        name={`questions.${index}.help`}
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center gap-2">
              <FormLabel>{t('surveys:dialog.fields.help')}</FormLabel>
            </div>
            <FormControl>
              <Textarea {...field} rows={2} placeholder={t('surveys:dialog.placeholders.help')} />
            </FormControl>
          </FormItem>
        )}
      />

      {HAS_OPTIONS.has(type) && (
        <OptionListEditor
          control={control}
          name={`questions.${index}.options`}
          retirableIds={retirableIds}
          keepOneActive={required}
          label={t('surveys:dialog.fields.options')}
          itemLabel={(n) => t('surveys:dialog.fields.option', { number: n })}
          addLabel={t('surveys:dialog.addOption')}
          max={SURVEY_MAX_OPTIONS}
          form={form}
          t={t}
        />
      )}

      {type === 'multi_choice' && (
        <div className="grid grid-cols-2 gap-4 md:w-1/2">
          <BoundInput form={form} name={`questions.${index}.min_selections`} label={t('surveys:dialog.fields.minSelections')} step={1} />
          <BoundInput form={form} name={`questions.${index}.max_selections`} label={t('surveys:dialog.fields.maxSelections')} step={1} />
          {rangeHint && <div className="col-span-2">{rangeHint}</div>}
        </div>
      )}

      {IS_GRID.has(type) && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <OptionListEditor
            control={control}
            name={`questions.${index}.rows`}
            retirableIds={retirableIds}
            keepOneActive={required}
            label={t('surveys:dialog.fields.rows')}
            itemLabel={(n) => t('surveys:dialog.fields.row', { number: n })}
            addLabel={t('surveys:dialog.addRow')}
            max={SURVEY_MAX_GRID_ROWS}
            min={1}
            form={form}
            t={t}
          />
          <OptionListEditor
            control={control}
            name={`questions.${index}.columns`}
            retirableIds={retirableIds}
            keepOneActive={required}
            label={t('surveys:dialog.fields.columns')}
            itemLabel={(n) => t('surveys:dialog.fields.column', { number: n })}
            addLabel={t('surveys:dialog.addColumn')}
            max={SURVEY_MAX_GRID_COLUMNS}
            form={form}
            t={t}
          />
        </div>
      )}

      {type === 'scale' && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <NumberSelect form={form} name={`questions.${index}.min`} label={t('surveys:dialog.fields.scaleMin')} choices={[0, 1]} />
          <NumberSelect form={form} name={`questions.${index}.max`} label={t('surveys:dialog.fields.scaleMax')} choices={SCALE_MAX_CHOICES} />
          <TextInput form={form} name={`questions.${index}.min_label`} label={t('surveys:dialog.fields.minLabel')} placeholder={t('surveys:dialog.placeholders.minLabel')} />
          <TextInput form={form} name={`questions.${index}.max_label`} label={t('surveys:dialog.fields.maxLabel')} placeholder={t('surveys:dialog.placeholders.maxLabel')} />
          {rangeHint && <div className="md:col-span-4">{rangeHint}</div>}
        </div>
      )}

      {type === 'rating' && (
        <div className="md:w-1/4">
          <NumberSelect form={form} name={`questions.${index}.max`} label={t('surveys:dialog.fields.ratingMax')} choices={RATING_MAX_CHOICES} />
          {rangeHint}
        </div>
      )}

      {type === 'number' && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:items-end">
          <BoundInput form={form} name={`questions.${index}.min`} label={t('surveys:dialog.fields.numberMin')} />
          <BoundInput form={form} name={`questions.${index}.max`} label={t('surveys:dialog.fields.numberMax')} />
          <FormField
            control={control}
            name={`questions.${index}.integer`}
            render={({ field }) => (
              <div className="flex h-10 items-center gap-2">
                <Checkbox
                  id={`q-${index}-integer`}
                  checked={field.value}
                  onCheckedChange={(checked) => field.onChange(checked === true)}
                />
                <Label htmlFor={`q-${index}-integer`} className="cursor-pointer font-normal">
                  {t('surveys:dialog.fields.integer')}
                </Label>
              </div>
            )}
          />
          {rangeHint && <div className="md:col-span-3">{rangeHint}</div>}
        </div>
      )}

      {type !== 'section' && (
        <FormField
          control={control}
          name={`questions.${index}.required`}
          render={({ field }) => (
            <div className="flex items-center gap-2">
              <Switch
                id={`q-${index}-required`}
                checked={field.value}
                onCheckedChange={field.onChange}
              />
              <Label htmlFor={`q-${index}-required`} className="cursor-pointer font-normal">
                {t('surveys:dialog.fields.required')}
              </Label>
            </div>
          )}
        />
      )}

      {renderExtras?.(index, type)}
    </div>
  );
}

/* -------------------------- small field helpers -------------------------- */

type ListName = `questions.${number}.options` | `questions.${number}.rows` | `questions.${number}.columns`;

interface OptionListEditorProps {
  control: Control<QuestionListValues>;
  name: ListName;
  label: string;
  itemLabel: (number: number) => string;
  addLabel: string;
  max: number;
  min?: number;
  form: UseFormReturn<QuestionListValues>;
  t: SurveyTranslate;
  /** The answered question's stored item ids: these retire instead of being removed. */
  retirableIds?: ReadonlySet<string>;
  /** A required question: its last active item cannot retire (the question would become unanswerable). */
  keepOneActive?: boolean;
}

function OptionListEditor({ control, name, label, itemLabel, addLabel, max, min = 2, form, t, retirableIds, keepOneActive = false }: OptionListEditorProps) {
  const { fields, append, remove, move } = useFieldArray({ control, name });
  const items = useWatch({ control, name });
  const activeCount = (items ?? []).filter((o) => o?.retired !== true).length;
  // Only stored items of an answered question retire; a required one keeps at least one active.
  const lastActiveLocked = keepOneActive && activeCount <= 1 && (items ?? []).some((o) => retirableIds?.has(o?.id ?? ''));
  const listError = (form.getFieldState(name, form.formState).error as { message?: string; root?: { message?: string } } | undefined);
  const listMessage = listError?.message ?? listError?.root?.message;
  return (
    <fieldset className="space-y-2">
      <legend className="mb-2 flex items-center gap-2">
        <span className={cn('text-sm font-medium', listMessage && 'text-destructive')}>{label}</span>
        <FieldError message={listMessage} />
      </legend>
      {fields.map((row, i) => (
        <FormField
          key={row.id}
          control={control}
          name={`${name}.${i}.label`}
          render={({ field, fieldState }) => {
            const itemRetired = items?.[i]?.retired === true;
            // `row.id` is the field array's own key: the option's id is in the values.
            const retirable = retirableIds?.has(items?.[i]?.id ?? '') === true;
            const toggleRetired = () =>
              form.setValue(`${name}.${i}.retired`, !itemRetired, { shouldDirty: true });
            return (
              <div className="flex items-center gap-2">
                <Input
                  {...field}
                  aria-label={itemLabel(i + 1)}
                  placeholder={itemLabel(i + 1)}
                  className={cn(fieldState.error && 'border-destructive', itemRetired && 'opacity-60')}
                />
                {itemRetired && <RetiredLabel t={t} />}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  disabled={i === 0}
                  onClick={() => move(i, i - 1)}
                  aria-label={t('surveys:dialog.moveOptionUp', { number: i + 1 })}
                  title={t('surveys:dialog.moveOptionUp', { number: i + 1 })}
                >
                  <ArrowUp className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  disabled={i === fields.length - 1}
                  onClick={() => move(i, i + 1)}
                  aria-label={t('surveys:dialog.moveOptionDown', { number: i + 1 })}
                  title={t('surveys:dialog.moveOptionDown', { number: i + 1 })}
                >
                  <ArrowDown className="h-4 w-4" />
                </Button>
                {retirable ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    // A required question's last active item would leave it unanswerable.
                    disabled={!itemRetired && lastActiveLocked}
                    onClick={toggleRetired}
                    aria-label={t(itemRetired ? 'surveys:dialog.restoreOption' : 'surveys:dialog.retireOption', { number: i + 1 })}
                    title={t(itemRetired ? 'surveys:dialog.restoreOption' : 'surveys:dialog.retireOption', { number: i + 1 })}
                  >
                    {itemRetired ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={fields.length <= min}
                    onClick={() => remove(i)}
                    aria-label={t('surveys:dialog.removeOption', { number: i + 1 })}
                    title={t('surveys:dialog.removeOption', { number: i + 1 })}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            );
          }}
        />
      ))}
      {lastActiveLocked && <LockHint>{t('surveys:dialog.keepOneActiveHint')}</LockHint>}
      <Button type="button" variant="ghost" size="sm" disabled={fields.length >= max} onClick={() => append(newOption())}>
        <Plus className="mr-1 h-4 w-4" />
        {addLabel}
      </Button>
    </fieldset>
  );
}

type NumberFieldName =
  | `questions.${number}.min`
  | `questions.${number}.max`
  | `questions.${number}.min_selections`
  | `questions.${number}.max_selections`;

function BoundInput({
  form,
  name,
  label,
  step = 'any',
}: {
  form: UseFormReturn<QuestionListValues>;
  name: NumberFieldName;
  label: string;
  step?: number | string;
}) {
  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem>
          <div className="flex items-center gap-2">
            <FormLabel>{label}</FormLabel>
            <FieldError message={fieldState.error?.message} />
          </div>
          <FormControl>
            <NumberInput
              value={field.value}
              onChange={(value) => field.onChange(value === '' ? '' : Number(value))}
              onBlur={field.onBlur}
              allowEmpty
              step={step}
              className={fieldState.error ? 'border-destructive' : undefined}
            />
          </FormControl>
        </FormItem>
      )}
    />
  );
}

function NumberSelect({
  form,
  name,
  label,
  choices,
}: {
  form: UseFormReturn<QuestionListValues>;
  name: `questions.${number}.min` | `questions.${number}.max`;
  label: string;
  choices: readonly number[];
}) {
  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem>
          <div className="flex items-center gap-2">
            <FormLabel>{label}</FormLabel>
            <FieldError message={fieldState.error?.message} />
          </div>
          <div>
            <Select value={field.value === '' ? undefined : String(field.value)} onValueChange={(v) => field.onChange(Number(v))}>
              <FormControl>
                <SelectTrigger aria-label={label}>
                  <SelectValue />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {choices.map((choice) => (
                  <SelectItem key={choice} value={String(choice)}>
                    {choice}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </FormItem>
      )}
    />
  );
}

function TextInput({
  form,
  name,
  label,
  placeholder,
}: {
  form: UseFormReturn<QuestionListValues>;
  name: `questions.${number}.min_label` | `questions.${number}.max_label`;
  label: string;
  placeholder?: string;
}) {
  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <div className="flex items-center gap-2">
            <FormLabel>{label}</FormLabel>
          </div>
          <FormControl>
            <Input {...field} placeholder={placeholder} />
          </FormControl>
        </FormItem>
      )}
    />
  );
}
